import trackError from 'src/lib/rollbar';
import { get } from 'lodash';
import {
  squaredInchesToSquaredFeet,
  squareInchesToSquareMeters,
  metersToInches,
  inchesToMeters,
  inchesToFeet,
} from 'src/lib/conversionUtils';
import constants from 'src/constants';
import featureFlags from 'src/featureFlags';

const { GLASS_ID, LEGACY_OPENINGS, OPENINGS, SASH_ID } = constants.labels;

/**
 * Class representing the openings of a building.
 * Openings (also called annotations) include windows, doors, shutter, vents, etc.
 * The constructor depends on data generated by a Hover.Building object.
 */
export default class Openings {
  /**
   * All param measurements are in inches.
   * @param {Array.<Hover.Annotation>} annotations openings of a building
   */
  constructor(annotations, elements, isRoofOnly, useWindowConsistentMeasurements) {
    this.annotations = annotations;
    this.elements = elements;
    this.isRoofOnly = isRoofOnly;
    this.useWindowConsistentMeasurements = useWindowConsistentMeasurements;
  }

  static nameDelimiter = '-';

  static missingNameDefault = 'Z0';

  /**
   * Return true if the item is a door or window based on its labels.
   * The labels array should only have length of one.
   * @param {Object} item
   * @param {Array} item.labels
   * @returns {boolean}
   * @private
   */
  isDoorOrWindow(item) {
    const { labels } = item;
    if (!Array.isArray(labels)) {
      trackError(`Labels must be an array. ${labels}`);
      return false;
    }
    if (labels.length < 1) {
      trackError(`Length of labels array must be at least 1. ${labels}`);
      return false;
    }
    if (labels.length > 1) {
      trackError(`Length of labels array should not be more than 1. ${labels}`);
    }
    // Synthetic annotations shouldn't appear, it's a temporary workaround
    // in machete to allow Sliding Glass Doors to work initially
    // See https://hoverinc.atlassian.net/browse/VIZ-488 for details
    if (get(item, 'parametricOptions.type') === 'slidingglass') {
      return false;
    }
    return LEGACY_OPENINGS.has(labels[0]);
  }

  /**
   * Return true if the item is a door or is in the provided label set
   * @param {Object} item
   * @param {Set} labels
   * @returns {boolean}
   * @private
   */
  isItemInLabelSet(item, labelSet) {
    const { labels } = item;
    // Synthetic annotations shouldn't appear, it's a temporary workaround
    // in machete to allow Sliding Glass Doors to work initially
    // See https://hoverinc.atlassian.net/browse/VIZ-488 for details
    if (get(item, 'parametricOptions.type') === 'slidingglass') {
      return false;
    }
    return labels.some(l => labelSet.has(l));
  }

  /**
   * Sort two items by name. The name of an item is
   * a string made up of a letter, delimiter and number.
   * @param {Object} item
   * @param {string} item.name e.g. W-2
   * @returns {number}
   * @private
   */
  sortItems(itemA, itemB) {
    const [letterA, numA] = itemA.name.split(Openings.nameDelimiter);
    const [letterB, numB] = itemB.name.split(Openings.nameDelimiter);
    if (letterA === letterB) {
      // e.g. 'W' === 'W'
      return Number(numA) - Number(numB);
    }
    if (letterA === 'D') return -1; // if door
    if (letterA === 'W') return 1; // if window
    return 0;
  }

  /**
   * Convert inches to object with inches, meters and feet
   * @param {number} inches
   * @returns {Object.<string, number>} inches, meters and feet
   * @private
   */
  getInchesInMultipleUnits(inches) {
    return {
      inches,
      feet: inchesToFeet(inches),
      meters: inchesToMeters(inches),
    };
  }

  /** * BEGINNING OF LEGACY SECTION
   *
   * These methods handle legacy data which exist on a very small number of jobs (mostly in staging).
   * The legacy data is the result of saving job styles using an older version of machete.
   * After updating machete, several properties on the item changed (i.e. name, width and height).
   * Since the amount of jobs with legacy data is very low and mostly in staging,
   * we can most likely remove this logic for handling legacy data in the future.
   */

  /**
   * Convert legacy item name to current item name
   * Legacy item names did not include a dash.
   * @param {string} legacyName
   * @returns {string}
   * @private
   */
  addDelimiterToLegacyDataName(legacyName) {
    if (legacyName === undefined) return Openings.missingNameDefault;
    return `${legacyName.slice(0, 1)}${Openings.nameDelimiter
      }${legacyName.slice(1)}`;
  }

  /**
   * Convert legacy item units to current units
   * @param {number} legacyUnit (in meters)
   * @returns {number} (in inches)
   * @private
   */
  convertLegacyItemUnits(legacyUnit) {
    if (legacyUnit === undefined) return 0;
    return metersToInches(legacyUnit);
  }

  /**
   * Reconcile legacy data which may exist on the item.
   * Currently name, width and height exist on the manowarData object on the item.
   * In the older version of machete, the properties were not
   * nested in the manowarData object. This method is necessary
   * to handle cases where we are dealing with legacy items.
   * @param {Object} item
   * @returns {Object} name, width, height, uuid
   * @private
   */
  reconcileLegacyData(item) {
    const measurementType = item.labels.some(label => label === SASH_ID)
      ? 'boundingBox'
      : 'snapped';
    return {
      uuid: item.uuid,
      labels: item.labels,
      name:
        get(item, 'manowarData.name') ||
        this.addDelimiterToLegacyDataName(item.name),
      // TODO: we will need to add some extra logic to access manowarData.width for window facades
      width: get(
        item,
        `manowarData.${measurementType}Width`,
        this.convertLegacyItemUnits(item.width),
      ),
      // TODO: we will need to add some extra logic to access manowarData.height for window facades
      height: get(
        item,
        `manowarData.${measurementType}Height`,
        this.convertLegacyItemUnits(item.height),
      ),
    };
  }

  /** * END OF LEGACY SECTION * */

  isGlass(item) {
    return item.labels.includes(GLASS_ID);
  }

  correctGlassDimensions(item) {
    const isGlass = this.isGlass(item);
    const WINDOW_CORRECTION = 5;

    return {
      ...item,
      width: isGlass ? item.width + WINDOW_CORRECTION : item.width,
      height: isGlass ? item.height + WINDOW_CORRECTION : item.height,
    };
  }

  /**
   * Get the items from annotations by filtering them,
   * mapping them, and sorting them.
   * @returns {Array}
   * @private
   */
  getItems() {
    if (this.isRoofOnly) return [];
    const useWindowConsistentMeasurements = featureFlags.shouldUseWindowConsistentMeasurements()
      && this.useWindowConsistentMeasurements;
    const elements = (useWindowConsistentMeasurements) ? this.elements : this.annotations;

    return elements
      .filter((item) =>
        useWindowConsistentMeasurements
          ? this.isItemInLabelSet(item, OPENINGS)
          : this.isDoorOrWindow(item),
      )
      .map((item) => this.reconcileLegacyData(item))
      .map((item) => this.correctGlassDimensions(item))
      .map(({ uuid, name, width, height }) => {
        return {
          uuid,
          name,
          width: this.getInchesInMultipleUnits(width),
          height: this.getInchesInMultipleUnits(height),
        };
      })
      .sort((a, b) => this.sortItems(a, b));
  }

  /**
   * Get the total area for items
   * @param {Array} items must have width and height properties in inches
   * @returns {Object.<string, number>}
   * @private
   */
  getTotalArea(items) {
    const inches = items.reduce((sum, item) => {
      const { width, height } = item;
      return sum + width.inches * height.inches;
    }, 0);

    return {
      inches,
      feet: squaredInchesToSquaredFeet(inches),
      meters: squareInchesToSquareMeters(inches),
    };
  }

  /**
   * Get the total area and count for items
   * @param {Array} items
   * @returns {Object}
   * @private
   */
  getTotals(items) {
    return {
      area: this.getTotalArea(items),
      count: items.length,
    };
  }

  /**
   * This is the only method in the class which is public.
   * It builds and formats the openings data.
   * @returns {Object.<string, Object>}
   * @public
   */
  getData() {
    const items = this.getItems();
    const total = this.getTotals(items);

    return { items, total };
  }
}
